/**
 * 公告系统 - 类型定义
 * 
 * 包含公告相关的所有类型定义
 * 
 * @author AI Assistant
 * @created 2025-12-04
 */

/**
 * 公告状态
 */
export enum AnnouncementStatus {
  /** 草稿 */
  DRAFT = 'DRAFT',
  /** 已发布 */
  PUBLISHED = 'PUBLISHED',
}

/**
 * 公告状态中文映射
 */
export const AnnouncementStatusLabel: Record<AnnouncementStatus, string> = {
  [AnnouncementStatus.DRAFT]: '草稿',
  [AnnouncementStatus.PUBLISHED]: '已发布',
}

/**
 * 公告状态颜色映射
 */
export const AnnouncementStatusColor: Record<AnnouncementStatus, string> = {
  [AnnouncementStatus.DRAFT]: 'warning',
  [AnnouncementStatus.PUBLISHED]: 'success',
}

/**
 * 公告优先级
 */
export enum AnnouncementPriority {
  /** 高优先级 */
  HIGH = 'HIGH',
  /** 普通优先级 */
  NORMAL = 'NORMAL',
  /** 低优先级 */
  LOW = 'LOW',
}

/**
 * 公告优先级中文映射
 */
export const AnnouncementPriorityLabel: Record<AnnouncementPriority, string> = {
  [AnnouncementPriority.HIGH]: '高优先级',
  [AnnouncementPriority.NORMAL]: '普通',
  [AnnouncementPriority.LOW]: '低优先级',
}

/**
 * 公告优先级颜色映射
 */
export const AnnouncementPriorityColor: Record<AnnouncementPriority, string> = {
  [AnnouncementPriority.HIGH]: 'danger',
  [AnnouncementPriority.NORMAL]: 'primary',
  [AnnouncementPriority.LOW]: 'info',
}

/**
 * 公告
 */
export interface Announcement {
  /** 公告 ID */
  id: number
  /** 公告标题 */
  title: string
  /** 公告内容 (支持 HTML) */
  content: string
  /** 状态 */
  status: AnnouncementStatus
  /** 优先级 */
  priority: AnnouncementPriority
  /** 是否弹窗显示 */
  isPopup: boolean
  /** 生效时间(UTC) */
  validFrom: string | null
  /** 失效时间(UTC) */
  validUntil: string | null
  /** 创建时间 */
  createdAt: string
  /** 更新时间 */
  updatedAt: string
}

/**
 * 创建公告请求
 */
export interface CreateAnnouncementRequest {
  /** 公告标题 */
  title: string
  /** 公告内容 (支持 HTML) */
  content: string
  /** 状态 */
  status: AnnouncementStatus
  /** 优先级 */
  priority: AnnouncementPriority
  /** 是否弹窗显示 */
  isPopup: boolean
  /** 生效时间(UTC) */
  validFrom: string | null
  /** 失效时间(UTC) */
  validUntil: string | null
}

/**
 * 更新公告请求
 */
export interface UpdateAnnouncementRequest {
  /** 公告标题 */
  title?: string
  /** 公告内容 (支持 HTML) */
  content?: string
  /** 状态 */
  status?: AnnouncementStatus
  /** 优先级 */
  priority?: AnnouncementPriority
  /** 是否弹窗显示 */
  isPopup?: boolean
  /** 生效时间(UTC) */
  validFrom?: string | null
  /** 失效时间(UTC) */
  validUntil?: string | null
}

/**
 * 公告列表查询参数
 */
export interface AnnouncementListParams {
  /** 页码 */
  page?: number
  /** 每页数量 */
  limit?: number
  /** 状态筛选 */
  status?: AnnouncementStatus
  /** 是否弹窗筛选 */
  isPopup?: boolean
  /** 优先级筛选 */
  priority?: AnnouncementPriority
}

/**
 * 公告列表响应
 */
export interface AnnouncementListResponse {
  /** 公告列表 */
  announcements: Announcement[]
  /** 总数 */
  total: number
  /** 当前页码 */
  page: number
  /** 每页数量 */
  limit: number
}

